import { useState, Dispatch, SetStateAction } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Stack, Link } from '@mui/material';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { Typography } from '@mui/material';
import Iconify from '../../components/Iconify';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import { Values } from '.';
import { updateResetPassword } from '../../store/users/api';
import { useAppDispatch } from '../../store';
import { useNavigate } from 'react-router-dom';
import { resetPassword } from '../../validation/resetPasswordSchema';

type Props = {
  setSteps: (val: number) => void;
  state: Values;
  setValues: Dispatch<SetStateAction<Values>>;
};

const Index: React.FC<Props> = ({ state, setValues }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {
    handleSubmit,
    getFieldProps,
    resetForm,
    isSubmitting,
    errors,
    touched,
    values: formValues,
  } = useFormik({
    initialValues: {
      ...state,
      confirmPassword: state.confirmPassword || '',
    },
    validationSchema: resetPassword,
    onSubmit: async (values) => {
      try {
        await dispatch(
          updateResetPassword({
            id: values.id,
            email: values.email,
            otp: values.otp,
            password: values.password,
          })
        ).unwrap();
        toast.success('Password has been successfully updated.');
        setValues((_) => ({
          id: '',
          email: '',
          otp: '',
          password: '',
          confirmPassword: '',
        }));
        resetForm();
        // Check if user came from company login - redirect to company-login, not admin login
        const fromCompanyLogin = localStorage.getItem('resetPasswordFromCompanyLogin') === 'true';
        // Clear the flag after checking
        if (fromCompanyLogin) {
          localStorage.removeItem('resetPasswordFromCompanyLogin');
        }
        // Default redirect to company-login (if flag was set or not set, always go to company-login)
        navigate('/company-login');
      } catch (error: any) {}
    },
  });

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleShowConfirmPassword = () => {
    setShowConfirmPassword((show) => !show);
  };

  // Check if password field is valid
  const isPasswordValid = touched.password && !errors.password && formValues.password;
  // Check if confirm password field is valid
  const isConfirmPasswordValid = touched.confirmPassword && !errors.confirmPassword && formValues.confirmPassword;

  return (
    <form
      autoComplete='off'
      noValidate
      onSubmit={handleSubmit}
    >
      <Stack spacing={3}>
        <TextField
          fullWidth
          autoComplete='username'
          type='email'
          label='Email address'
          {...getFieldProps('email')}
          error={Boolean(touched.email && errors.email)}
          helperText={touched.email && errors.email}
        />
        <TextField
          fullWidth
          autoComplete='username'
          type='text'
          label='OTP'
          {...getFieldProps('otp')}
          error={Boolean(touched.otp && errors.otp)}
          helperText={touched.otp && errors.otp}
        />
        <TextField
          fullWidth
          autoComplete='new-password'
          type={showPassword ? 'text' : 'password'}
          label='Password'
          {...getFieldProps('password')}
          error={Boolean(touched.password && errors.password)}
          helperText={touched.password && errors.password}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                {isPasswordValid && (
                  <Iconify
                    icon='eva:checkmark-circle-2-fill'
                    sx={{ color: 'success.main', mr: 1 }}
                  />
                )}
                <IconButton
                  onClick={handleShowPassword}
                  edge='end'
                >
                  <Iconify
                    icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          autoComplete='new-password'
          type={showConfirmPassword ? 'text' : 'password'}
          label='Confirm Password'
          {...getFieldProps('confirmPassword')}
          error={Boolean(touched.confirmPassword && errors.confirmPassword)}
          helperText={touched.confirmPassword && errors.confirmPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                {isConfirmPasswordValid && (
                  <Iconify
                    icon='eva:checkmark-circle-2-fill'
                    sx={{ color: 'success.main', mr: 1 }}
                  />
                )}
                <IconButton
                  onClick={handleShowConfirmPassword}
                  edge='end'
                >
                  <Iconify
                    icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ my: 1 }}
      ></Stack>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
      >
        <LoadingButton
          fullWidth
          size='large'
          type='submit'
          variant='contained'
          loading={isSubmitting}
        >
          Save your password
        </LoadingButton>
      </Stack>
      <Typography
        variant='body2'
        align='center'
        sx={{
          mt: 3,
        }}
      >
        Back to&nbsp;
        <Link
          variant='subtitle2'
          component={RouterLink}
          to={localStorage.getItem('resetPasswordFromCompanyLogin') === 'true' ? '/company-login' : '/login'}
          underline='hover'
        >
          Login
        </Link>
      </Typography>
    </form>
  );
};

export default Index;
